import React from 'react';
import { salesRoute } from '@/modules/sales/route';
import { getFromDateDefault, getToDateDefault } from '@/shared/utils/dates';
import { createRoute } from '@tanstack/react-router';
import * as z from 'zod';

const dateSchema = z.string().regex(/^\d{2}-\d{2}-\d{4}$/, 'Date must be in DD-MM-YYYY format');

const storageKey = 'charges-search-params'; // * Key to store search params in session storage, we will use safe constants for this later on
const resetStorageKey = 'charges-reset-flag'; // * Key to determine if we need to reset the search params to default values

export const paymentStatusEnum = z.enum([
  'Waiting',
  'Pending',
  'Processing',
  'Success',
  'Partial',
  'Failed',
  'Void',
  'Error',
  'Min Price',
]);

const chargesSearchSchema = z.object({
  page: z.coerce.number().min(1).default(1),
  size: z.coerce.number().min(50).default(50),
  from: dateSchema.default(getFromDateDefault), // * Start date for the charges (default: 1 month ago)
  to: dateSchema.default(getToDateDefault), // * End date for the charges (default: today)
  sort: z
    .enum(['vehicle', 'timestamp-start', 'timestamp-stop', 'net-price'])
    .default('timestamp-start'),
  order: z.enum(['asc', 'desc']).default('desc'), // * Sort order
  hide: z.array(z.string()).optional(), // * Columns to hide <- We can restrict this to specific column ids per route if needed
  group: z.array(z.string()).optional(), // * User groups filter
  status: z.array(paymentStatusEnum).optional(), // * Payment status filter
  txn: z.string().optional(), // * Transaction ID filter
  veh: z.string().optional(), // * Vehicle license plate filter
  amount: z.string().optional(), // * Amount filter
  user: z.string().optional(), // * User email filter
  cp: z.string().optional(), // * Charge point name filter
  regd: z.enum(['true', 'false']).optional(), // * Registered user filter
  addr: z.string().optional(), // * Chargepoint address filter
});

export type chargesSearchSchemaType = z.infer<typeof chargesSearchSchema>;

export const chargesRoute = createRoute({
  getParentRoute: () => salesRoute,
  path: 'charges',
  component: React.lazy(async () => await import('@/app/routes/Sales/charges/charge-list')),
  wrapInSuspense: true,
  validateSearch: (search = {}) => {
    const isResetting = sessionStorage.getItem(resetStorageKey);
    const stored = sessionStorage.getItem(storageKey);

    if (stored && !isResetting) {
      return chargesSearchSchema.parse({ ...JSON.parse(stored), ...search });
    }

    if (isResetting) {
      sessionStorage.removeItem(resetStorageKey);
      sessionStorage.removeItem(storageKey);
    }

    return chargesSearchSchema.parse(search);
  },
  onError: () => {
    sessionStorage.setItem('charges-had-error', 'true');
  },
  onLeave: (ctx) => {
    // * If the reset flag is present, remove it and skip saving the state
    if (sessionStorage.getItem(resetStorageKey)) {
      return;
    }
    // * If there was an error, remove the flag and skip saving the state
    if (sessionStorage.getItem('charges-had-error')) {
      sessionStorage.removeItem('charges-had-error');
      return;
    }
    sessionStorage.setItem(storageKey, JSON.stringify(ctx.search));
  },
});
